import { PrintButton } from "@/Common/Components/Buttons/PrintButton";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";

import styles from "./EncountersFilter.module.scss";

interface IEncountersFilterProps {
    model: IPatientMedicalSummaryModel;
    isReadOnly?: boolean;
    displayPrintButton?: boolean;
    fromMyleOperation?: number;
    toMyleOperation?: number;
    encounterSearchTermChangedOperation?: number;
    isPatientNoteDisabled?: boolean;
    id?: string;
    fromChange?: (date: Date) => void;
    toChange?: (date: Date) => void;
    newNote: (e: React.MouseEvent) => void;
    showCriteria: () => void;
    toggleDisplayOnlyUnsigned: () => void;
    onClickCallbackPrintNotes?: () => void;
    onEncounterSearchTermChanged?: (textOrDate: any) => void;
}

export function EncountersFilter(props: IEncountersFilterProps): JSX.Element {
    const { getText } = useTranslation();
    const showPrintBtn = !props.isReadOnly && props.displayPrintButton;
    let toggleIconCss = "toggle-icon2";
    if (props.model.get("IsDisplayingOnlyUnsigned"))
        toggleIconCss += " active";

    return (
        <div className="note-search">
            <div className="flex-container">
                <div className="search-field width-100 margin-xs-right">
                    <div className="gf">
                        <span className="glyphicon glyphicon-search" />
                    </div>
                    <TextboxControl
                        dataCy="medicalNote-search"
                        debounceMs={400}
                        value={props.model.get("EncounterSearchTerm")}
                        className="fullWidth"
                        operation={props.encounterSearchTermChangedOperation}
                        onChange={(textOrDate: any) => {
                            if (props.onEncounterSearchTermChanged) {
                                props.onEncounterSearchTermChanged(textOrDate);
                            }
                        }}
                        placeholder={getText("SearchLabel")}
                    />
                </div>
                {showPrintBtn && (
                    <PrintButton
                        className="button control push-right-5"
                        rightMargin={5}
                        onClickCallback={() => props.onClickCallbackPrintNotes?.()}
                    />
                )}
            </div>

            {props.model.get("IsSearchCriteriaExpanded") && (
                <div className="margin-sm-top">
                    <MultiDateSelector
                        id={"doc_search_date_selector" + (props.id ?? "")}
                        dataCy="medicalNote-search-dates"
                        fullWidth={true}
                        fromMyleOperation={props.fromMyleOperation}
                        toMyleOperation={props.toMyleOperation}
                        fromChange={props.fromChange ? date => props.fromChange(date) : null}
                        toChange={props.toChange ? date => props.toChange(date) : null}
                        fromSetTodayLabel={getText("TodayLabel")}
                        toSetTodayLabel={getText("TodayLabel")}
                        from={props.model.get("EncounterSearchDateFrom")}
                        to={props.model.get("EncounterSearchDateTo")}
                    />
                </div>
            )}

            <div className="clearfix margin-md-top">
                {!props.isReadOnly && (
                    <>
                        <span
                            data-cy="medicalNote-filterUnsigned"
                            className={`${toggleIconCss} ${styles.filterUnsigned}`}
                            onClick={() => props.toggleDisplayOnlyUnsigned()}
                        />
                        {!props.isPatientNoteDisabled && (
                            <span>
                                <a
                                    className="margin-sm-left"
                                    id="create_new_note"
                                    data-cy="medicalNote-create"
                                    onClick={e => props.newNote(e)}
                                >  {getText("AddNewNoteLabel")}
                                </a>
                            </span>
                        )}
                    </>
                )}
                <div className="pull-right">
                    <a
                        className="margin-sm-left"
                        onClick={() => props.showCriteria()}
                        data-cy="medicalNote-search-criterias"
                    >
                        [{props.model.get("IsSearchCriteriaExpanded") ? "-" : "+"}]&nbsp; {getText("CriteriaLabel")}
                    </a>
                </div>
            </div>
        </div>
    );
}
